import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useState } from 'react';
import { listBotRuns } from '../../api/endpoints';
import { ApiError } from '../../api/envelope';
import type { BotRunStatus } from '../../api/types';
import { Badge, type BadgeTone } from '../../components/Badge';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { RefreshButton } from '../../components/RefreshButton';
import { Spinner } from '../../components/Spinner';
import { fmtDurationMs, fmtTokens } from '../../lib/format';
import { useTimeFormat } from '../../lib/tenantTime';
import { useLoad } from '../../lib/useLoad';

/** The run log: every time the assistant looked at a conversation, and what it did. */

const RUN_TONE: Record<BotRunStatus, BadgeTone> = {
  completed: 'ok',
  resolved: 'ok',
  handoff: 'accent',
  failed: 'danger',
  stale: 'neutral',
  skipped_budget: 'warn',
  preview: 'neutral',
  draft: 'accent',
};

const RUN_LABEL: Record<BotRunStatus, string> = {
  completed: 'Replied',
  resolved: 'Resolved',
  handoff: 'Handed off',
  failed: 'Failed',
  stale: 'Superseded',
  skipped_budget: 'Budget cap',
  preview: 'Playground',
  draft: 'Drafted',
};

export function RunsCard() {
  const { fmtDateTime } = useTimeFormat();
  const [page, setPage] = useState(1);
  const [filterInput, setFilterInput] = useState('');
  const [filter, setFilter] = useState('');
  const runs = useLoad(() => listBotRuns(page, filter || undefined), [page, filter]);

  const notFound = runs.error instanceof ApiError && runs.error.kind === 'notFound';

  return (
    <section className="card card-pad manage-card">
      <div className="bot-runs-head">
        <div>
          <h2 className="manage-card-title">Recent runs</h2>
          <p className="manage-card-sub">Every time the assistant looked at a conversation.</p>
        </div>
        <form
          className="bot-runs-filter"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setFilter(filterInput.trim());
          }}
        >
          <input
            className="input"
            placeholder="Conversation #"
            inputMode="numeric"
            value={filterInput}
            onChange={(e) => setFilterInput(e.target.value)}
          />
          <button className="btn btn-secondary" type="submit" aria-label="Filter runs">
            <Search size={14} />
          </button>
          {/* The assistant keeps running while this page is open, so the log is
              the one thing here that is out of date the moment it paints. */}
          <RefreshButton
            refreshing={runs.refreshing}
            busy={runs.stale}
            onClick={runs.reload}
            title="Refresh runs"
            label={null}
          />
        </form>
      </div>

      {runs.stale ? (
        <Spinner />
      ) : notFound ? (
        <Note tone="warn">No conversation with that number.</Note>
      ) : runs.error ? (
        <ErrorNote error={runs.error} />
      ) : !runs.data?.length ? (
        <p className="manage-card-sub">
          {page > 1 ? 'No more runs.' : filter ? 'No runs for that conversation.' : 'No runs yet.'}
        </p>
      ) : (
        <div className="bot-runs-scroll">
          <table className="bot-runs-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Outcome</th>
                <th title="Internal id — not the conversation number shown in the dashboard">
                  Conv. (internal)
                </th>
                <th>Tokens</th>
                <th>Took</th>
                <th>Tools</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.map((run) => (
                <tr key={run.id}>
                  <td>{fmtDateTime(run.createdAt)}</td>
                  <td>
                    <Badge tone={RUN_TONE[run.status] ?? 'neutral'}>
                      {RUN_LABEL[run.status] ?? run.status}
                    </Badge>
                    {run.error && <div className="bot-run-error">{run.error}</div>}
                  </td>
                  <td>{run.conversationId ?? '—'}</td>
                  <td>{fmtTokens(run.inputTokens, run.outputTokens)}</td>
                  <td>{fmtDurationMs(run.durationMs)}</td>
                  <td>{run.toolsUsed.length ? run.toolsUsed.join(', ') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bot-runs-pager">
        <button
          className="btn btn-ghost"
          disabled={page <= 1 || runs.loading}
          onClick={() => setPage((p) => p - 1)}
        >
          <ChevronLeft size={14} />
          Newer
        </button>
        <span className="field-hint">Page {page}</span>
        <button
          className="btn btn-ghost"
          disabled={runs.loading || (runs.data?.length ?? 0) < 25}
          onClick={() => setPage((p) => p + 1)}
        >
          Older
          <ChevronRight size={14} />
        </button>
      </div>
    </section>
  );
}
